import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { CcfLogger } from '@nice-ccf/agent-sdk';
import { LocalStorageHelper, NotificationSettings } from '@nice-ccf/core-sdk';
import {
  CcfBox,
  CcfInfoIcon,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { SetItemHelper, SetSwitchHelper } from '../ccf-settings-switch-item';
import { CcfSwitchItem } from '@nice-ccf/ui-controls';
import styles from './ccf-login-voice-preferance-tab.module.scss';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import {
  getEnableLyraDesign,
  getPartnerAccessTabVisibilityState,
  getSimulatorTabVisibilityState,
  globalActions,
} from '../../../global.app.slice';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlayDisabledIcon from '@mui/icons-material/PlayDisabled';
import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeUp from '@mui/icons-material/VolumeUp';
import { useSelector, useDispatch } from 'react-redux';
import CcfAudioElement from '../ccf-audio-element/ccf-audio-element';
import { Button, Checkbox, FormControlLabel, FormGroup, Switch } from '@mui/material';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { authorizePartnersAccess } from '../../../login/mock-data/authorizePartnersAccess';
import { selectLoginCredentialsState } from '../../../login/+state/login.slice';
import KeyboardVoiceOutlinedIcon from '@mui/icons-material/KeyboardVoiceOutlined';
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface CcfAudioWithSink extends HTMLAudioElement {
  setSinkId: (deviceId: string) => void;
}

export const mockSoundFile = [
  '',
  '/assets/audio/Ring1.mp3',
  '/assets/audio/Ring2.mp3',
  '/assets/audio/Ring3.mp3',
];

/**
 * @example CcfLoginVoicePreferanceTab()
 * @returns
 */
export function CcfLoginVoicePreferanceTab() {
  const ringtoneAudio = useRef(null);
  const [autoAccept, setAutoAccept] = useState(false);
  const [delay, setDelay] = useState(0);
  const [deviceOptions, setDevices] = useState<DeviceInteface[]>([]);
  const [secondaryDevice, setSecondaryDevice] = useState(0);
  const [selectedDevice, setSelectedDevice] = useState('No Devices');
  const [tone, setTone] = useState(1);
  const [translate] = useTranslator();
  const [volume, setVolume] = useState(80);
  const isSimulatorTabVisible = useSelector(getSimulatorTabVisibilityState);
  const isPartnerAccessTabVisible = useSelector(
    getPartnerAccessTabVisibilityState
  );
  const [simulatorTabCheck, setSimulatorTabCheck] = useState(
    isSimulatorTabVisible
  );
  const [partnerAccessTabCheck, setPartnerAccessTabCheck] = useState(
    isPartnerAccessTabVisible
  );
  const [calenderCheck, setcalenderCheck] = useState(true);
  const [workspaceCheck, setWorkspaceCheck] = useState(true);
  const [crmCheck, setCrmCheck] = useState(true);
  const enableLyraDesign = useSelector(getEnableLyraDesign);
  const [isNoiseCancellationChecked, setIsNoiseCancellationChecked] =
    useState(true);
  const [isNoiseCancellationDisabled, setIsNoiseCancellationDisabled] =
    useState(true);
  const dispatch = useDispatch();
  const { setSimulatorTabVisibilityState, setPartnerAccessTabVisibilityState } =
    globalActions;

  const constraints = {
    audio: true,
    video: false,
  };

  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const loginCredentials = useSelector(selectLoginCredentialsState);
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const loginCredentialsArray = loginCredentialsFromSSO?.split(' ');
  const assignName = isAuthenticated
    ? `${
        loginCredentialsArray[0].charAt(0).toUpperCase() +
        loginCredentialsArray[0].slice(1).toLowerCase()
      } ${
        loginCredentialsArray[1].charAt(0).toUpperCase() +
        loginCredentialsArray[1].slice(1).toLowerCase()
      }`
    : loginCredentials?.firstName + ' ' + loginCredentials?.lastName;

  const ccfLogger: CcfLogger = new CcfLogger(
    'App.consumer',
    'CcfVoiceSettings'
  );

  interface DeviceInteface {
    id: string;
    label: string;
  }

  /**
   *
   * @param event - Callback fired when the state is changed.
   * @param checked - If `true`, the component appears selected. Checked is passed in by MUI component <FormControlLabel />
   * @example handleChange
   *
   */
  const handleAutoAccept = (
    event: React.SyntheticEvent<Element, Event>,
    checked: boolean
  ) => {
    LocalStorageHelper.setItem(NotificationSettings.AUTO_ACCEPT, checked);
    setAutoAccept(checked as boolean);
  };

  /**
   *
   * @param event - Volume
   * @param newVolume - new volume setting
   * @example handleVolumeChange()
   */
  const handleVolumeChange = (event: Event, newVolume: number | number[]) => {
    const volumeValue = Array.isArray(newVolume) ? newVolume[0] : newVolume;
    const volumeQuotient = volumeValue / 100;

    setVolume(volumeQuotient as number);

    LocalStorageHelper.setItem(
      NotificationSettings.SOFTPHONE_VOLUME,
      volumeQuotient
    );
  };

  /**
   *
   * @param event - Ringtone change
   * @example handleRingTonechange()
   */
  const handleRingToneChange = (event: SelectChangeEvent<number>) => {
    setTone(event.target.value as number);
    LocalStorageHelper.setItem(
      NotificationSettings.RINGTONE,
      event.target.value
    );
  };

  /**
   *
   * @param event - Secondary Device
   * @example handleSecondaryDeviceChange
   */
  const handleSecondaryDeviceChange = (event: SelectChangeEvent<number>) => {
    setSecondaryDevice(event.target.value as number);
    LocalStorageHelper.setItem(
      NotificationSettings.SECONDARY_DEVICE,
      event.target.value
    );
  };

  /**
   * Handles the change event for the selected device dropdown.
   * @param event - The change event triggered by the dropdown.
   */
  const handleSelectedDeviceChange = (
    event: any) => {
    setSelectedDevice(event.target.value);  
    };

  /**
   *
   * @param event - Delay is seconds
   * @example handleDelaySecondschange
   */
  const handleDelaySecondsChange = (event: SelectChangeEvent<number>) => {
    setDelay(event.target.value as number);
    LocalStorageHelper.setItem(
      NotificationSettings.SECONDARY_DEVICE_DELAY,
      event.target.value
    );
  };

  /**
   *
   * @param event - play ringtone file
   * @example onPlayClick
   */
  const onPlayClick = (event: React.SyntheticEvent, secondary?: boolean) => {
    if (!ringtoneAudio.current) {
      return;
    }

    const audio: CcfAudioWithSink = ringtoneAudio.current;

    // Pause any existing audio from ref
    audio.pause();
    // Set the audio on every click in case it was changed between clicks
    audio.src = mockSoundFile[tone];
    audio.setSinkId('default');
    audio.play();
    // }
  };

  /**
   * Updating Voice Preferance
   * @param newTabIndex -number
   * @example - handleVoicePreferance()
   */
  const toggleSimulatorTabVisibilty = (simulatorVisibilityState: boolean) => {
    setSimulatorTabCheck(!simulatorVisibilityState);
    dispatch(setSimulatorTabVisibilityState(!simulatorVisibilityState));
  };

  /**
   * Updating Voice Preferance
   * @param newTabIndex -number
   * @example - handleVoicePreferance()
   */
  const togglePartnerAccessTabVisibilty = (
    partnerAccessVisibilityState: boolean
  ) => {
    setPartnerAccessTabCheck(!partnerAccessVisibilityState);
    dispatch(setPartnerAccessTabVisibilityState(!partnerAccessVisibilityState));
  };

  /**
   *
   * @example populateFromStorate()
   */
  const populateFromStorage = () => {
    SetItemHelper(NotificationSettings.SECONDARY_DEVICE_DELAY, setDelay);
    SetItemHelper(NotificationSettings.SOFTPHONE_VOLUME, setVolume);
    SetItemHelper(NotificationSettings.RINGTONE, setTone);
    SetSwitchHelper(NotificationSettings.AUTO_ACCEPT, setAutoAccept);
  };

  useEffect(() => {
    const devices: DeviceInteface[] = [];

    navigator.mediaDevices.getUserMedia(constraints).then(() => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return;
      } else {
        navigator.mediaDevices.enumerateDevices().then((mediaDevices) => {
          const outputDevices = mediaDevices.filter((device) => {
            return device.kind === 'audiooutput';
          });

          outputDevices.forEach(function (outputDevice, index) {
            const device: DeviceInteface = { id: '', label: '' };

            device.id = outputDevice.deviceId;
            device.label = outputDevice.label || 'audio out ' + index;
            devices.push(device);
          });

          setDevices(devices);
          SetItemHelper(
            NotificationSettings.SECONDARY_DEVICE,
            setSecondaryDevice
          );
        });
      }
    });

    populateFromStorage();

    return () => {
      ccfLogger.info('voice settings', ' Close');
    };
  }, []);

  return (
    <CcfBox className={styles.loginVoicePreferanceContainer}>
      <CcfBox className={styles.settings_col}>
        <CcfAudioElement ref={ringtoneAudio} />
        <List
          sx={{ width: '100%', bgcolor: 'background.paper' }}
          subheader={
            <ListSubheader sx={{ position: 'static' }}>
              {translate('softPhone')}{' '}
            </ListSubheader>
          }
        >
          <ListItem>
            <Box sx={{ width: 225 }}>
              <Stack
                spacing={2}
                direction="row"
                sx={{ mb: 1 }}
                alignItems="center"
              >
                <VolumeDown />
                <Slider
                  aria-label="Volume"
                  value={volume * 100}
                  onChange={handleVolumeChange}
                />
                <VolumeUp />
              </Stack>
            </Box>
          </ListItem>
          <CcfSwitchItem
            text="autoAccept"
            icon={<PhoneInTalkIcon style={{ fill: '#606870' }} />}
            handleChange={handleAutoAccept}
            name={NotificationSettings.AUTO_ACCEPT}
            isChecked={autoAccept}
            isDisabled={false}
          />

          <ListItem>
            <Grid container direction="row" alignItems="center">
              <FormControl sx={{ m: 1, minWidth: 200 }} disabled={autoAccept}>
                <InputLabel id="demo-simple-select-disabled-label">
                  {translate('ringTone')}
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={tone}
                  label={translate('ringTone')}
                  onChange={handleRingToneChange}
                  sx={{
                    '&& fieldset': {
                      border: '1px solid #c4c4c4',
                    },
                  }}
                >
                  <MenuItem value={0}>{translate('none')}</MenuItem>
                  <MenuItem value={1}>{translate('ring1')}</MenuItem>
                  <MenuItem value={2}>{translate('ring2')}</MenuItem>
                  <MenuItem value={3}>{translate('ring3')}</MenuItem>
                </Select>
              </FormControl>
              <Tooltip
                describeChild
                enterDelay={500}
                title={
                  autoAccept ? translate('disabled') : translate('playRingtone')
                }
              >
                {autoAccept ? (
                  <IconButton aria-label={translate('disabled')} disabled>
                    <PlayDisabledIcon style={{ fill: '#606870' }} />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={onPlayClick}
                    aria-label={translate('playRingtone')}
                  >
                    <PlayArrowIcon style={{ fill: '#606870' }} />
                  </IconButton>
                )}
              </Tooltip>
            </Grid>
          </ListItem>
        </List>
        <List
          sx={{ width: '100%', bgcolor: 'background.paper' }}
          subheader={
            <ListSubheader sx={{ position: 'static' }}>
              {translate('secondaryRinger')}
            </ListSubheader>
          }
        >
          <ListItem>
            <Grid container direction="row" alignItems="center">
              <FormControl sx={{ m: 1, minWidth: 200 }}>
                <InputLabel id="demo-simple-select-disabled-label">
                  {translate('secondaryDevice')}
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={secondaryDevice}
                  label={translate('secondaryDevice')}
                  onChange={handleSecondaryDeviceChange}
                  disabled={autoAccept}
                >
                  <MenuItem value={0}>{translate('none')}</MenuItem>
                  {deviceOptions.map((option) => {
                    return (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Tooltip
                describeChild
                enterDelay={500}
                title={
                  autoAccept ? translate('disabled') : translate('playRingtone')
                }
              >
                {autoAccept || secondaryDevice.toString() === '0' ? (
                  <IconButton aria-label={translate('disabled')} disabled>
                    <PlayDisabledIcon
                      style={{ fill: '#rgba(0, 0, 0, 0.26);' }}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={(e) => onPlayClick(e, true)}
                    aria-label={translate('playRingtone')}
                  >
                    <PlayArrowIcon style={{ fill: '#606870' }} />
                  </IconButton>
                )}
              </Tooltip>
            </Grid>
          </ListItem>
          <ListItem>
            <FormControl sx={{ m: 1, minWidth: 200 }}>
              <InputLabel id="delay-seconds-disabled-label">
                {translate('delaySeconds')}
              </InputLabel>
              <Select
                labelId="delay-seconds-label"
                id="delay-seconds-select"
                value={delay}
                label={translate('delaySeconds')}
                onChange={handleDelaySecondsChange}
                disabled={autoAccept}
                variant="outlined"
                sx={{
                  '&& fieldset': {
                    border: '1px solid #c4c4c4',
                  },
                }}
              >
                <MenuItem value={0}>{translate('none')}</MenuItem>
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
                <MenuItem value={4}>4</MenuItem>
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={6}>6</MenuItem>
                <MenuItem value={7}>7</MenuItem>
                <MenuItem value={8}>8</MenuItem>
                <MenuItem value={9}>9</MenuItem>
                <MenuItem value={10}>10</MenuItem>
              </Select>
            </FormControl>
          </ListItem>
        </List>
        <List
          sx={{ width: '87%', bgcolor: 'background.paper' }}
          subheader={
            <ListSubheader sx={{ position: 'static' }}>
              Noise Cancellation
            </ListSubheader>
          }
        >
          <div style={{ paddingLeft: '16px', display: 'inline-flex' }}>
          <InfoOutlinedIcon />
            <p
              style={{
                paddingLeft: '5px',
                color: 'rgb(97, 108, 117)',
                fontWeight: 500,
                fontSize: '0.875rem',
                marginTop: '0px',
              }}
            >
              Note: To use the Noise Cancellation <br></br>
              feature, it is recommended to install the<br></br>
              CXone Noise Cancellation Extension<br></br>
              from the Chrome Web Store.
              <a
                href="https://help.nice-incontact.com/PoC.htm#cshid=CXANCExt"
                target="_blank"
                rel="noreferrer"
              >
                Click here for details
              </a>
            </p>
          </div>
            <ListItem sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <div
              style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '8px',
              width: '100%',
              }}
            >
              <KeyboardVoiceOutlinedIcon style={{ fontSize: '24px' }} />
              <Switch
              checked={isNoiseCancellationChecked}
              disabled={isNoiseCancellationDisabled}
              />
            </div>
            <div 
              style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              }}
            >
              <CcfTypography
              variant="h6"
              style={{
                fontSize: '14px',
                color: '#606870',
                fontWeight: 500,
              }}
              >
              Mic Sensitivity
              </CcfTypography>
              <Box sx={{ width: '55%' }}>
              <Stack
                spacing={2}
                direction="row"
                sx={{ mb: 1 }}
                alignItems="center"
              >
                <Slider
                aria-label="Volume"
                value={90}
                onChange={handleVolumeChange}
                disabled
                />
              </Stack>
              </Box>
            </div>
            </ListItem>
            <ListItem sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <div
              style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '8px',
              width: '100%',
              }}
            >
              <KeyboardVoiceOutlinedIcon style={{ fontSize: '24px' }} />
              <Switch
              checked={isNoiseCancellationChecked}
              disabled={isNoiseCancellationDisabled}
              />
            </div>
            <div 
              style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              }}
            >
              <CcfTypography
              variant="h6"
              style={{
                fontSize: '14px',
                color: '#606870',
                fontWeight: 500,
              }}
              >
              Speaker Sensitivity
              </CcfTypography>
              <Box sx={{ width: '55%' }}>
              <Stack
                spacing={2}
                direction="row"
                sx={{ mb: 1 }}
                alignItems="center"
              >
                <Slider
                aria-label="Volume"
                value={70}
                onChange={handleVolumeChange}
                disabled
                />
              </Stack>
              </Box>
            </div>
            </ListItem>
        </List>
      </CcfBox>
      <CcfBox className={styles.settings_col}>
    <Typography variant="body1" sx={{ fontSize: '1em', fontWeight: 700, paddingTop: '1rem' }}>
        Jabra Call Control
      </Typography>
      <Typography variant='body1' sx={{ padding: '0.5rem 0rem'}}>
      Use your headset to answer and end calls, mute and other features supported by your headset.
      </Typography>
      <List>
      <Typography
        variant="body1"
        sx={{ width: "100%", fontSize: '1em', fontWeight: 700, color: 'rgb(25, 28, 33)' }}>
          Add Devices
        </Typography>
      <Typography variant="body1" sx={{ marginBottom: 2, padding: '0.5rem 0rem' }}>
        Choose Add Devices button to select which headset(s) you will be using.
        You will be asked to provide consent before adding devices.
      </Typography>
      <Button
        variant="outlined"
        style={{
          fontSize: '0.875rem',
          lineHeight: 1.75,
          minWidth: '64px',
          color: 'rgb(0, 124, 190)',
          backgroundColor: 'rgb(255, 255, 255)',
          boxShadow: 'rgb(218, 226, 232) 0px 2px 0px',
          fontWeight: 700,
          textDecoration: 'none',
          borderRadius: '4px',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'rgb(218, 226, 232)',
          padding: '0.125rem 1rem',
          marginBottom: '1rem',
        }}
      >
        Add Devices
      </Button>
    </List>
    <List
    >
      <Typography
        variant="body1"
        sx={{ width: "100%",fontSize: '1em', fontWeight: 700, color: 'rgb(25, 28, 33)' }}>
          Selected Devices</Typography>
      <Typography variant="body1" sx={{ padding: '0.5rem 0rem'}}>
        Any added devices will appear here.
      </Typography>
      <FormControl sx={{ minWidth: 200 }}>
                <Select
                   labelId="current-selected-headset-label"
                   id="current-selected-headset-select"
                   value={selectedDevice}
                   onChange={handleSelectedDeviceChange}
                >    
                      <MenuItem key="nodevices" value="No Devices">
                      No Devices
                      </MenuItem>
                </Select>
              </FormControl>
    </List>
      </CcfBox>
      <CcfBox className={styles.settings_col}>
        <CcfBox mt={'30px !important'} display={'flex'}>
          <CcfTypography className={styles.tabTitle} sx={{ fontSize: '1.5rem' }}>
            Launch on Login
          </CcfTypography>
          <CcfInfoIcon className={styles.infoIcon} sx={{ fontSize: '32px' }} />
        </CcfBox>

        <CcfBox>
          <FormControl>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox />}
                label={translate('enableLyraDesign')}
                labelPlacement="end"
                checked={enableLyraDesign}
                onChange={() => dispatch(globalActions.setEnableLyraDesign(!enableLyraDesign))}
                className={styles.label}
              />
              <FormControlLabel
                control={<Checkbox />}
                label={translate('showSimulatorButton')}
                labelPlacement="end"
                checked={simulatorTabCheck}
                onChange={() => toggleSimulatorTabVisibilty(simulatorTabCheck)}
                className={styles.label}
              />
              <FormControlLabel
                control={<Checkbox />}
                label={translate('openCalender')}
                labelPlacement="end"
                checked={calenderCheck}
                onChange={() => setcalenderCheck(!calenderCheck)}
                className={styles.label}
              />
              <FormControlLabel
                control={<Checkbox />}
                label={translate('showCustomWorkSpace')}
                labelPlacement="end"
                checked={workspaceCheck}
                onChange={() => setWorkspaceCheck(!workspaceCheck)}
                className={styles.label}
              />
              <FormControlLabel
                control={<Checkbox />}
                label={translate('openCRM')}
                labelPlacement="end"
                checked={crmCheck}
                onChange={() => setCrmCheck(!crmCheck)}
                className={styles.label}
              />
              {authorizePartnersAccess.find(
                (x: any) => assignName && x === assignName
              ) && (
                <FormControlLabel
                  control={<Checkbox />}
                  label={translate('showPartnerAccessButton')}
                  labelPlacement="end"
                  checked={partnerAccessTabCheck}
                  onChange={() =>
                    togglePartnerAccessTabVisibilty(partnerAccessTabCheck)
                  }
                  className={styles.label}
                />
              )}
            </FormGroup>
          </FormControl>
        </CcfBox>
      </CcfBox>
    </CcfBox>
  );
}

export default CcfLoginVoicePreferanceTab;
